// ============================================
// LogoutButton — Lightweight logout for shell-free pages
// Used on onboarding pages where AppShell (TopNav) is hidden
// ============================================

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Popover from '@components/ui/Popover/Popover';
import Avatar from '@components/ui/Avatar/Avatar';
import { useAuthStore } from '@/stores/authStore';
import { logout } from '@/services/authService';
import { broadcastLogout } from '@/hooks/useAuthBroadcast';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './LogoutButton.module.scss';

// Caret down icon (same as TopNav)
function CaretDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 6L8 10L12 6"
        stroke="#536171"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Logout icon (arrow exiting box, same as TopNav)
function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5M13.3333 14.1667L17.5 10M17.5 10L13.3333 5.83333M17.5 10H7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface LogoutButtonProps {
  className?: string;
}

function LogoutButton({ className }: LogoutButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const clearSession = useAuthStore((s) => s.clearSession);
  const [open, setOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const firstName = session?.user.firstName ?? '';
  const lastName = session?.user.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const email = session?.user.email ?? '';

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setOpen(false);
    try {
      await logout();
    } finally {
      // See TopNav.handleLogout — must remove the cached session before
      // clearSession() to prevent __root.tsx re-hydrating the auth store.
      queryClient.removeQueries({ queryKey: ['session'] });
      broadcastLogout();
      clearSession();
      const redirectPath = window.location.pathname + window.location.search;
      const loginUrl =
        redirectPath && redirectPath !== '/' && redirectPath !== '/login'
          ? `/login?redirect_to=${encodeURIComponent(redirectPath)}`
          : '/login';
      window.location.href = loginUrl;
    }
  }, [clearSession, queryClient, isLoggingOut]);

  const dropdownContent = (
    <div className={styles.dropdown}>
      <div className={styles.userInfo}>
        <Avatar name={fullName} size="md" />
        <div className={styles.userDetails}>
          <span className={styles.userName}>{fullName || t('K_FALLBACK_USER', 'User')}</span>
          <span className={styles.userEmail}>{email}</span>
        </div>
      </div>
      <div className={styles.divider} />
      <button
        type="button"
        className={styles.logoutItem}
        onClick={() => void handleLogout()}
        disabled={isLoggingOut}
        aria-busy={isLoggingOut}
      >
        <LogoutIcon />
        {isLoggingOut ? t('K_LOGGING_OUT', 'Logging out...') : t('K_LOGOUT', 'Logout')}
      </button>
    </div>
  );

  return (
    <Popover
      content={dropdownContent}
      placement="bottom-end"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <button
        type="button"
        className={`${styles.trigger} ${className ?? ''}`.trim()}
        disabled={isLoggingOut}
        aria-busy={isLoggingOut}
      >
        <Avatar name={fullName} size="sm" />
        <span className={styles.triggerName}>{fullName || t('K_FALLBACK_USER', 'User')}</span>
        <CaretDownIcon />
      </button>
    </Popover>
  );
}

export default LogoutButton;
